import { DrilldownFieldType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import _ from 'lodash'

import { isValidValueForType } from './utils'

export interface ValidationResult {
  isValid: boolean
  error: string | null
}

const VALIDATION_ERROR_MESSAGES: Record<DrilldownFieldType, string> = {
  number: 'Value must be a valid number',
  boolean: 'Value must be either "true" or "false"',
  date: 'Value must be a valid date in YYYY-MM-DD format',
  object: 'Value must be a JSON object (not an array or primitive)',
  array: 'Value must be a JSON array',
  text: '',
  enum: '',
} as const

/**
 * Validates a value based on its field type
 * @param value - The value to validate (should be a string for validation)
 * @param type - The field type to validate against
 * @returns ValidationResult with validation status and error message
 */
export const validateFieldValue = (
  value: string,
  type: DrilldownFieldType
): ValidationResult => {
  if (!value || _.isEmpty(value)) {
    return { isValid: true, error: null }
  }

  const isValid = isValidValueForType(value, type)

  if (isValid) {
    return { isValid: true, error: null }
  }

  const errorMessage = VALIDATION_ERROR_MESSAGES[type]

  // For text and enum types, they're always valid
  // The enum side of this should be revisited once we pre-define possible keys for enums
  if (type === 'text' || type === 'enum') {
    return { isValid: true, error: null }
  }

  return {
    isValid: false,
    error: errorMessage,
  }
}

/**
 * Gets the validation status for use in antd components
 * @param value - The value to validate
 * @param type - The field type
 * @returns 'error' if invalid, undefined if valid
 */
export const getValidationStatus = (
  value: string,
  type: DrilldownFieldType
): 'error' | undefined => {
  const validation = validateFieldValue(value, type)
  return validation.error ? 'error' : undefined
}
